package er.prateek.crossly.game.riddle

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import er.prateek.crossly.data.model.riddle.Riddle
import er.prateek.crossly.data.model.riddle.RiddleSet
import kotlin.math.ceil

private const val MAX_ENTRY_LENGTH = 6

/**
 * Pure game-state machine for one rapid-fire riddle set: step through the questions, take a
 * numeric answer per question, count correct/mistakes. A wrong answer or a per-question timeout
 * a timeout counts as a miss and advances; a wrong answer holds the question until it's right. Mirrors the other engines' shape
 * (observable state, private setters, an `isComplete` flag).
 */
class RiddleRunEngine(
    val set: RiddleSet,
    /** Share of questions that must be right to clear the set (`modes.riddles.pass_ratio`). */
    passRatio: Double = DEFAULT_PASS_RATIO,
) {
    val total: Int = set.riddles.size

    var index: Int by mutableStateOf(0)
        private set
    var correctCount: Int by mutableStateOf(0)
        private set
    var mistakes: Int by mutableStateOf(0)
        private set
    var isComplete: Boolean by mutableStateOf(set.riddles.isEmpty())
        private set

    /** The player's in-progress answer for the current question. */
    var entry: String by mutableStateOf("")
        private set

    /** Bumps on every wrong submit so the UI can react (shake, buzz, toast) even to repeats. */
    var wrongTick: Int by mutableStateOf(0)
        private set

    /** The question the latest wrong submit was on, so the UI can drop feedback for one already left. */
    var lastWrongIndex: Int = -1
        private set

    val current: Riddle? get() = set.riddles.getOrNull(index)

    /** Correct answers needed to clear the set. */
    val passMark: Int = passMarkFor(total, passRatio)

    /**
     * Whether the finished run clears the level. A run that mostly timed out isn't a clear: it
     * earns no stars, unlocks nothing and doesn't count toward streaks or badges.
     */
    val passed: Boolean get() = isComplete && correctCount >= passMark

    /** Whether the in-progress entry already equals the answer, so the caller can auto-submit. */
    val isEntryCorrect: Boolean get() = current?.let { entry.toIntOrNull() == it.answer } == true

    fun appendDigit(ch: Char) {
        if (!ch.isDigit() || isComplete ) return
        if (entry.length >= MAX_ENTRY_LENGTH) return
        entry += ch
    }

    fun backspace() {
        if (entry.isNotEmpty()) entry = entry.dropLast(1)
    }

    fun clearEntry() {
        entry = ""
    }

    /**
     * Submits the current entry. Returns whether it was correct. A correct answer advances; a wrong
     * one counts a mistake, clears the entry and stays on the same question until it's answered
     * right (or the per-question timer runs out).
     */
    fun submit(): Boolean {
        val riddle = current ?: return false
        val correct = entry.toIntOrNull() == riddle.answer
        if (correct) {
            correctCount++
            advance()
        } else {
            mistakes++
            lastWrongIndex = index
            wrongTick++
            entry = ""
        }
        return correct
    }

    /** The per-question timer ran out: a miss, then move on. */
    fun timeout() {
        if (current == null) return
        mistakes++
        advance()
    }

    private fun advance() {
        entry = ""
        if (index + 1 >= total) {
            isComplete = true
        } else {
            index++
        }
    }

    companion object {
        const val DEFAULT_PASS_RATIO = 0.5

        /**
         * [ratio] of [total], rounded up -- and never zero, so an empty set can't pass. The small
         * nudge down keeps float noise from rounding up a whole question (10 x 0.7 is 7.0000001).
         */
        fun passMarkFor(total: Int, ratio: Double = DEFAULT_PASS_RATIO): Int =
            ceil(total * ratio - 1e-9).toInt().coerceIn(1, total.coerceAtLeast(1))
    }
}
